'use client';

import { useEffect } from 'react';

import { usePathname, useSearchParams } from 'next/navigation';

import { AnalyticsEvents, analytics } from '@kit/analytics';
import {
  AppEvent,
  AppEventType,
  ConsumerProvidedEventTypes,
  useAppEvents,
} from '@kit/shared/events';
import { isBrowser } from '@kit/shared/utils';
import { useNrfUserSettings } from '@kit/supabase/hooks/use-nrf-user-settings';
import { useCookieConsent } from '@kit/ui/cookie-banner';

type AnalyticsMapping<
  T extends ConsumerProvidedEventTypes = NonNullable<unknown>,
> = {
  [K in AppEventType<T>]?: (event: AppEvent<T, K>) => unknown;
};

/**
 * Hook to subscribe to app events and map them to analytics actions
 * @param mapping
 */
function useAnalyticsMapping<T extends ConsumerProvidedEventTypes>(
  mapping: AnalyticsMapping<T>,
) {
  const appEvents = useAppEvents<T>();

  useEffect(() => {
    const subscriptions = Object.entries(mapping).map(
      ([eventType, handler]) => {
        appEvents.on(eventType as AppEventType<T>, handler);

        return () => appEvents.off(eventType as AppEventType<T>, handler);
      },
    );

    return () => {
      subscriptions.forEach((unsubscribe) => unsubscribe());
    };
  }, [appEvents, mapping]);
}

/**
 * Define a mapping of app events to analytics actions
 * Add new mappings here to track new events in the analytics service from app events
 */
const analyticsMapping: AnalyticsMapping = {
  'user.signedIn': (event) => {
    const { userId, ...traits } = event.payload;

    if (userId) {
      return analytics.identify(userId, traits);
    }
  },
  'user.signedUp': (event) => {
    return analytics.trackEvent(AnalyticsEvents.UserSignedUp, event.payload);
  },
  'checkout.started': (event) => {
    return analytics.trackEvent(AnalyticsEvents.CheckoutStarted, event.payload);
  },
};

function AnalyticsProviderBrowser(props: React.PropsWithChildren) {
  const { status: consentStatus } = useCookieConsent();
  const { isImpersonated, betaFeatures } = useNrfUserSettings();

  const isEnabled =
    betaFeatures.disable_analytics !== true &&
    consentStatus === 'accepted' &&
    !isImpersonated;

  // Only enable analytics when the user has accepted cookies
  useEffect(() => {
    if (isEnabled) {
      // Initialize the Google Analytics provider after consent
      void analytics.addProvider('google-analytics' as never, {} as never);
    } else {
      analytics.removeProvider('google-analytics' as never);
    }
  }, [isEnabled]);

  // Subscribe to app events and map them to analytics actions
  useAnalyticsMapping(isEnabled ? analyticsMapping : {});

  // Report page views to the analytics service
  useReportPageView((url) => {
    if (!isEnabled) {
      return;
    }

    return analytics.trackPageView(url);
  });

  // Render children
  return props.children;
}

/**
 * Provider for the analytics service
 */
export function AnalyticsProvider(props: React.PropsWithChildren) {
  if (!isBrowser()) {
    return props.children;
  }

  return <AnalyticsProviderBrowser>{props.children}</AnalyticsProviderBrowser>;
}

/**
 * Hook to report page views to the analytics service
 * @param reportAnalyticsFn
 */
function useReportPageView(reportAnalyticsFn: (url: string) => unknown) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    const url = [pathname, searchParams.toString()].filter(Boolean).join('?');

    reportAnalyticsFn(url);
  }, [pathname, reportAnalyticsFn, searchParams]);
}
